import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { Spinner, Button, Form, FormGroup, Input, FormFeedback } from 'reactstrap';
import Modal from 'react-bootstrap/Modal';
import DataTable from 'react-data-table-component';
import { AsyncSearchableSelect, SearchableSelect } from '../../../shared/layout/forms/AppSelect';
import { MdEdit, MdDelete, MdSearch, MdAdd } from 'react-icons/md';
import { fetchWarrantyTemplates, fetchWarrantyTemplateTypes, fetchOrgCategories, fetchManufacturers, fetchManufacturerItems, createWarrantyTemplate, updateWarrantyTemplate, deleteWarrantyTemplate } from '../../../services/Http/Api';
import StyledCard from '../../../shared/layout/cards/StyledCard';
import MainButton from '../../../shared/layout/buttons/MainButton';

const WarrantyTemplatesList = () => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterCategory, setFilterCategory] = useState(null);
    const [filterType, setFilterType] = useState(null);
    const [templates, setTemplates] = useState([]);
    const [pagination, setPagination] = useState({ current_page: 1, total: 0, per_page: 10, last_page: 1 });
    const [categories, setCategories] = useState([]);
    const [templateTypes, setTemplateTypes] = useState([]);
    const [manufacturers, setManufacturers] = useState([]);
    const [manufacturerItems, setManufacturerItems] = useState([]);
    const [manufacturerItemsPagination, setManufacturerItemsPagination] = useState({ current_page: 1, total: 0, per_page: 10, last_page: 1 });
    const [manufacturerItemsSearchTerm, setManufacturerItemsSearchTerm] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        duration_months: '',
        terms: '',
        claim_process: '',
        warranty_template_type_id: null,
        category_id: null,
        applicability_type: null,
        applicability_id: null,
        is_active: true
    });
    const [formErrors, setFormErrors] = useState({});
    const [applicabilityOptions] = useState([
        { value: 'all', label: t('WarrantyTemplates.applicabilityAll') },
        { value: 'category', label: t('WarrantyTemplates.applicabilityCategory') },
        { value: 'manufacturer', label: t('WarrantyTemplates.applicabilityManufacturer') },
        { value: 'manufacturer_item', label: t('WarrantyTemplates.applicabilityManufacturerItem') }
    ]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [typesResponse, categoriesResponse, manufacturersResponse, itemsResponse, templatesResponse] = await Promise.all([
                    fetchWarrantyTemplateTypes().catch(err => {
                        console.error('Error fetching warranty template types:', err);
                        return [];
                    }),
                    fetchOrgCategories().catch(err => {
                        console.error('Error fetching categories:', err);
                        return [];
                    }),
                    fetchManufacturers({ page: 1, perPage: 1000 }).catch(err => {
                        console.error('Error fetching manufacturers:', err);
                        return { data: [] };
                    }),
                    fetchManufacturerItems({ searchTerm: manufacturerItemsSearchTerm }).catch(err => {
                        console.error('Error fetching manufacturer items:', err);
                        return { data: [], current_page: 1, total: 0, per_page: 10, last_page: 1 };
                    }),
                    fetchWarrantyTemplates({ page: pagination.current_page, perPage: pagination.per_page }).catch(err => {
                        console.error('Error fetching templates:', err);
                        return { data: [], current_page: 1, total: 0, per_page: 10, last_page: 1 };
                    })
                ]);
                setTemplateTypes(Array.isArray(typesResponse) ? typesResponse.map(type => ({ value: type.id, label: type.name })) : []);
                setCategories(Array.isArray(categoriesResponse) ? categoriesResponse.map(cat => ({ value: cat.id, label: cat.path || cat.name })) : []);
                setManufacturers(Array.isArray(manufacturersResponse.data) ? manufacturersResponse.data.map(man => ({ value: man.id, label: man.name })) : []);
                const itemsArray = Array.isArray(itemsResponse)
                    ? itemsResponse
                    : Array.isArray(itemsResponse.data)
                        ? itemsResponse.data
                        : [];
                setManufacturerItems(itemsArray.map(item => ({ value: item.id, label: item.product_name || item.model_number || item.id })));
                setManufacturerItemsPagination({
                    current_page: 1,
                    total: itemsArray.length,
                    per_page: itemsArray.length,
                    last_page: 1
                });
                setTemplates(Array.isArray(templatesResponse.data) ? templatesResponse.data : []);
                setPagination({
                    current_page: templatesResponse.current_page || 1,
                    total: templatesResponse.total || 0,
                    per_page: templatesResponse.per_page || 10,
                    last_page: templatesResponse.last_page || 1
                });
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [pagination.current_page, filterCategory, filterType, manufacturerItemsPagination.current_page, manufacturerItemsSearchTerm]);

    const validateJson = (value) => {
        if (!value) return true;
        try {
            JSON.parse(value);
            return true;
        } catch {
            return false;
        }
    };

    const validateForm = () => {
        const errors = {};
        if (!formData.name) errors.name = t('WarrantyTemplates.errors.nameRequired');
        if (!formData.duration_months || formData.duration_months < 1) errors.duration_months = t('WarrantyTemplates.errors.durationInvalid');
        if (!formData.warranty_template_type_id) errors.warranty_template_type_id = t('WarrantyTemplates.errors.typeRequired');
        if (!formData.category_id) errors.category_id = t('WarrantyTemplates.errors.categoryRequired');
        if (formData.terms && !validateJson(formData.terms)) errors.terms = t('WarrantyTemplates.errors.invalidJson');
        if (formData.claim_process && !validateJson(formData.claim_process)) errors.claim_process = t('WarrantyTemplates.errors.invalidJson');
        if (!formData.applicability_type) errors.applicability_type = t('WarrantyTemplates.errors.applicabilityTypeRequired');
        if (formData.applicability_type && formData.applicability_type.value !== 'all' && !formData.applicability_id) {
            errors.applicability_id = t('WarrantyTemplates.errors.applicabilityIdRequired');
        }
        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleFormChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        setFormErrors(prev => ({ ...prev, [field]: null }));
    };

    const handleSearchChange = (e) => {
        setSearchTerm(e.target.value);
    };

    const loadCategoryOptions = async (inputValue) => {
        try {
            const response = await fetchOrgCategories(inputValue);
            return Array.isArray(response)
                ? response.map(cat => ({ value: cat.id, label: cat.path || cat.name }))
                : [];
        } catch (error) {
            console.error('Error loading categories:', error);
            return [];
        }
    };

    const handleManufacturerItemsSearchChange = (e) => {
        setManufacturerItemsSearchTerm(e.target.value);
        setManufacturerItemsPagination(prev => ({ ...prev, current_page: 1 }));
    };

    const handlePageChange = (page) => {
        setPagination(prev => ({ ...prev, current_page: page }));
    };

    const handleManufacturerItemsPageChange = (page) => {
        setManufacturerItemsPagination(prev => ({ ...prev, current_page: page }));
    };

    const handleCreateSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        try {
            const applicable_to = formData.applicability_type?.value === 'all' ? null : {
                [formData.applicability_type.value]: formData.applicability_id?.value
            };
            const data = {
                name: formData.name,
                description: formData.description,
                duration_months: parseInt(formData.duration_months),
                terms: formData.terms ? JSON.parse(formData.terms) : null,
                claim_process: formData.claim_process ? JSON.parse(formData.claim_process) : null,
                warranty_template_type_id: formData.warranty_template_type_id?.value,
                category_core_id: formData.category_id?.value || null,
                applicable_to,
                is_active: formData.is_active
            };
            console.log('Submitting create template:', data); // Debug
            await createWarrantyTemplate(data);
            setShowCreateModal(false);
            fetchTemplates();
            setFormData({
                name: '',
                description: '',
                duration_months: '',
                terms: '',
                claim_process: '',
                warranty_template_type_id: null,
                category_id: null,
                applicability_type: null,
                applicability_id: null,
                is_active: true
            });
            setFormErrors({});
        } catch (error) {
            console.error('Error creating template:', error);
            setFormErrors({ general: error.message || t('WarrantyTemplates.errors.createFailed') });
        } finally {
            setLoading(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;
        setLoading(true);
        try {
            const applicable_to = formData.applicability_type?.value === 'all' ? null : {
                [formData.applicability_type.value]: formData.applicability_id?.value
            };
            const data = {
                name: formData.name,
                description: formData.description,
                duration_months: parseInt(formData.duration_months),
                terms: formData.terms ? JSON.parse(formData.terms) : null,
                claim_process: formData.claim_process ? JSON.parse(formData.claim_process) : null,
                warranty_template_type_id: formData.warranty_template_type_id?.value,
                category_core_id: formData.category_id?.value || null,
                applicable_to,
                is_active: formData.is_active
            };
            console.log('Submitting edit template:', data); // Debug
            await updateWarrantyTemplate(selectedTemplate.id, data);
            setShowEditModal(false);
            fetchTemplates();
            setFormErrors({});
        } catch (error) {
            console.error('Error updating template:', error);
            setFormErrors({ general: error.message || t('WarrantyTemplates.errors.updateFailed') });
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setLoading(true);
        try {
            await deleteWarrantyTemplate(selectedTemplate.id);
            setShowDeleteModal(false);
            fetchTemplates();
        } catch (error) {
            console.error('Error deleting template:', error);
            setFormErrors({ general: error.message || t('WarrantyTemplates.errors.deleteFailed') });
        } finally {
            setLoading(false);
        }
    };

    const openEditModal = (template) => {
        setSelectedTemplate(template);
        setFormData({
            name: template.name || '',
            description: template.description || '',
            duration_months: template.duration_months || '',
            terms: template.terms ? JSON.stringify(template.terms, null, 2) : '',
            claim_process: template.claim_process ? JSON.stringify(template.claim_process, null, 2) : '',
            warranty_template_type_id: templateTypes.find(type => type.value === template.warranty_template_type_id) || null,
            category_id: template.category_core_id ? categories.find(cat => cat.value === template.category_core_id) : null,
            applicability_type: template.applicable_to
                ? Object.keys(template.applicable_to)[0] === 'category' ? { value: 'category', label: t('WarrantyTemplates.applicabilityCategory') }
                    : Object.keys(template.applicable_to)[0] === 'manufacturer' ? { value: 'manufacturer', label: t('WarrantyTemplates.applicabilityManufacturer') }
                        : Object.keys(template.applicable_to)[0] === 'manufacturer_item' ? { value: 'manufacturer_item', label: t('WarrantyTemplates.applicabilityManufacturerItem') }
                            : { value: 'all', label: t('WarrantyTemplates.applicabilityAll') }
                : { value: 'all', label: t('WarrantyTemplates.applicabilityAll') },
            applicability_id: template.applicable_to
                ? {
                    category: categories.find(cat => cat.value === template.applicable_to.category),
                    manufacturer: manufacturers.find(man => man.value === template.applicable_to.manufacturer),
                    manufacturer_item: manufacturerItems.find(item => item.value === template.applicable_to.manufacturer_item)
                }[Object.keys(template.applicable_to)[0]] || null
                : null,
            is_active: template.is_active || true
        });
        setShowEditModal(true);
    };

    const fetchTemplates = async () => {
        setLoading(true);
        try {
            const response = await fetchWarrantyTemplates({ page: pagination.current_page, perPage: pagination.per_page });
            console.log('Fetched templates:', response.data); // Debug
            setTemplates(response.data);
            setPagination({
                current_page: response.current_page || 1,
                total: response.total || 0,
                per_page: response.per_page || 10,
                last_page: response.last_page || 1
            });
        } catch (error) {
            console.error('Error fetching templates:', error);
        } finally {
            setLoading(false);
        }
    };

    const getApplicabilityOptions = () => {
        switch (formData.applicability_type?.value) {
            case 'category':
                return categories;
            case 'manufacturer':
                return manufacturers;
            case 'manufacturer_item':
                return manufacturerItems;
            default:
                return [];
        }
    };

    const columns = [
        { name: t('WarrantyTemplates.name'), selector: row => row.name || '-', sortable: true },
        { name: t('WarrantyTemplates.duration'), selector: row => row.duration_months || '-', sortable: true },
        { name: t('WarrantyTemplates.category'), selector: row => row.category_core?.path || row.category_core?.name || '-', sortable: true },
        { name: t('WarrantyTemplates.type'), selector: row => row.type?.name || '-', sortable: true },
        { name: t('WarrantyTemplates.status'), selector: row => row.is_active ? t('WarrantyTemplates.active') : t('WarrantyTemplates.inactive'), sortable: true },
        {
            name: t('WarrantyTemplates.actions'),
            cell: row => (
                <ActionContainer>
                    <ActionButton onClick={() => openEditModal(row)} aria-label={t('WarrantyTemplates.edit')}>
                        <MdEdit className="action-icon" />
                    </ActionButton>
                    <ActionButton onClick={() => {
                        setSelectedTemplate(row);
                        setShowDeleteModal(true);
                    }} aria-label={t('WarrantyTemplates.delete')}>
                        <MdDelete className="action-icon" />
                    </ActionButton>
                </ActionContainer>
            )
        }
    ];

    const manufacturerItemsColumns = [
        { name: t('WarrantyTemplates.manufacturerItemId'), selector: row => row.id || '-', sortable: true },
        { name: t('WarrantyTemplates.manufacturerItemName'), selector: row => row.product_name || '-', sortable: true }
    ];

    const filteredTemplates = templates.filter(template =>
        template.name?.toLowerCase().includes(searchTerm.toLowerCase()) &&
        (!filterCategory || template.category_core_id === filterCategory.value) &&
        (!filterType || template.warranty_template_type_id === filterType.value)
    );

    const customStyles = {
        table: { style: { backgroundColor: 'transparent' } },
        headRow: {
            style: {
                backgroundColor: '#f9fafb',
                minHeight: '52px',
                borderBottom: '1px solid #e5e7eb',
            },
        },
        headCells: {
            style: {
                fontSize: '12px',
                fontWeight: '600',
                color: '#374151',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
            },
        },
        rows: {
            style: {
                minHeight: '72px',
                '&:not(:last-of-type)': {
                    borderBottom: '1px solid #f3f4f6',
                },
                '&:hover': {
                    backgroundColor: '#f9fafb !important',
                },
            },
        },
    };

    return (
        <>
            <StyledCard noPadding>
                <ControlsContainer>
                    <div style={{ display: 'flex', gap: '1rem', flex: 1, flexWrap: 'wrap' }}>
                        <SearchContainer>
                            <MdSearch size={20} />
                            <input
                                type="text"
                                placeholder={t('WarrantyTemplates.searchPlaceholder')}
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                        </SearchContainer>
                        <div style={{ width: '12.5rem' }}>
                            <AsyncSearchableSelect
                                cacheOptions
                                defaultOptions={categories}
                                loadOptions={loadCategoryOptions}
                                value={filterCategory}
                                onChange={(_, __, option) => setFilterCategory(option)}
                                placeholder={t('WarrantyTemplates.filterCategory')}
                                isClearable
                                styles={{ control: (base) => ({ ...base, minHeight: '2.375rem', borderRadius: '0.5rem', border: '1px solid #e5e7eb' }) }}
                            />
                        </div>
                    </div>
                    <MainButton size="small" onClick={() => setShowCreateModal(true)} icon={<MdAdd />}>
                        {t('WarrantyTemplates.create')}
                    </MainButton>
                </ControlsContainer>

                {loading ? (
                    <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280' }}>
                        {t('WarrantyTemplates.loading')}
                    </div>
                ) : (
                    <DataTable
                        columns={columns}
                        data={filteredTemplates}
                        pagination
                        paginationServer
                        paginationTotalRows={pagination.total}
                        paginationPerPage={pagination.per_page}
                        paginationComponentOptions={{
                            rowsPerPageText: t('WarrantyTemplates.rowsPerPage'),
                            rangeSeparatorText: t('WarrantyTemplates.of'),
                            noRowsPerPage: true
                        }}
                        onChangePage={handlePageChange}
                        highlightOnHover
                        noHeader
                        customStyles={customStyles}
                        noDataComponent={<EmptyState>{t('WarrantyTemplates.noTemplates')}</EmptyState>}
                    />
                )}
            </StyledCard>

            {/* Create Modal */}
            <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('WarrantyTemplates.createTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formErrors.general && <FormFeedback style={{ display: 'block', marginBottom: '0.625rem' }} invalid>{formErrors.general}</FormFeedback>}
                    <StyledForm onSubmit={handleCreateSubmit}>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.name')}</StyledLabel>
                            <Input
                                type="text"
                                value={formData.name}
                                onChange={e => handleFormChange('name', e.target.value)}
                                invalid={!!formErrors.name}
                                required
                            />
                            <FormFeedback>{formErrors.name}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.description')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.description}
                                onChange={e => handleFormChange('description', e.target.value)}
                            />
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.durationMonths')}</StyledLabel>
                            <Input
                                type="number"
                                value={formData.duration_months}
                                onChange={e => handleFormChange('duration_months', e.target.value)}
                                invalid={!!formErrors.duration_months}
                                required
                            />
                            <FormFeedback>{formErrors.duration_months}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.terms')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.terms}
                                onChange={e => handleFormChange('terms', e.target.value)}
                                invalid={!!formErrors.terms}
                                placeholder='{"key": "value"}'
                            />
                            <FormFeedback>{formErrors.terms}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.claimProcess')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.claim_process}
                                onChange={e => handleFormChange('claim_process', e.target.value)}
                                invalid={!!formErrors.claim_process}
                                placeholder='{"step": "description"}'
                            />
                            <FormFeedback>{formErrors.claim_process}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.type')}</StyledLabel>
                            <SearchableSelect
                                options={templateTypes}
                                value={formData.warranty_template_type_id}
                                onChange={(_, __, opt) => handleFormChange('warranty_template_type_id', opt)}
                                styles={formErrors.warranty_template_type_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                required
                            />
                            {formErrors.warranty_template_type_id && <FormFeedback style={{ display: 'block' }}>{formErrors.warranty_template_type_id}</FormFeedback>}
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.category')}</StyledLabel>
                            <AsyncSearchableSelect
                                cacheOptions
                                defaultOptions={categories}
                                loadOptions={loadCategoryOptions}
                                value={formData.category_id}
                                onChange={(_, __, opt) => handleFormChange('category_id', opt)}
                                styles={formErrors.category_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                isClearable
                                required
                            />
                            {formErrors.category_id && <FormFeedback style={{ display: 'block' }}>{formErrors.category_id}</FormFeedback>}
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.applicabilityType')}</StyledLabel>
                            <SearchableSelect
                                options={applicabilityOptions}
                                value={formData.applicability_type}
                                onChange={(_, __, opt) => {
                                    handleFormChange('applicability_type', opt);
                                    handleFormChange('applicability_id', null);
                                }}
                                styles={formErrors.applicability_type ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                required
                            />
                            {formErrors.applicability_type && <FormFeedback style={{ display: 'block' }}>{formErrors.applicability_type}</FormFeedback>}
                        </StyledFormGroup>
                        {formData.applicability_type && formData.applicability_type.value !== 'all' && (
                            <StyledFormGroup>
                                <StyledLabel>{t('WarrantyTemplates.applicabilityId')}</StyledLabel>
                                <SearchableSelect
                                    options={getApplicabilityOptions()}
                                    value={formData.applicability_id}
                                    onChange={(_, __, opt) => handleFormChange('applicability_id', opt)}
                                    styles={formErrors.applicability_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                    required
                                />
                                {formErrors.applicability_id && <FormFeedback style={{ display: 'block' }}>{formErrors.applicability_id}</FormFeedback>}
                            </StyledFormGroup>
                        )}
                        <StyledFormGroup check>
                            <StyledLabel check>
                                <Input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={e => handleFormChange('is_active', e.target.checked)}
                                />
                                {t('WarrantyTemplates.isActive')}
                            </StyledLabel>
                        </StyledFormGroup>
                        <SaveButton type="submit" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : t('WarrantyTemplates.submit')}
                        </SaveButton>
                    </StyledForm>
                </Modal.Body>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('WarrantyTemplates.editTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formErrors.general && <FormFeedback style={{ display: 'block', marginBottom: '10px' }} invalid>{formErrors.general}</FormFeedback>}
                    <StyledForm onSubmit={handleEditSubmit}>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.name')}</StyledLabel>
                            <Input
                                type="text"
                                value={formData.name}
                                onChange={e => handleFormChange('name', e.target.value)}
                                invalid={!!formErrors.name}
                                required
                            />
                            <FormFeedback>{formErrors.name}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.description')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.description}
                                onChange={e => handleFormChange('description', e.target.value)}
                            />
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.durationMonths')}</StyledLabel>
                            <Input
                                type="number"
                                value={formData.duration_months}
                                onChange={e => handleFormChange('duration_months', e.target.value)}
                                invalid={!!formErrors.duration_months}
                                required
                            />
                            <FormFeedback>{formErrors.duration_months}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.terms')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.terms}
                                onChange={e => handleFormChange('terms', e.target.value)}
                                invalid={!!formErrors.terms}
                                placeholder='{"key": "value"}'
                            />
                            <FormFeedback>{formErrors.terms}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.claimProcess')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.claim_process}
                                onChange={e => handleFormChange('claim_process', e.target.value)}
                                invalid={!!formErrors.claim_process}
                                placeholder='{"step": "description"}'
                            />
                            <FormFeedback>{formErrors.claim_process}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.type')}</StyledLabel>
                            <SearchableSelect
                                options={templateTypes}
                                value={formData.warranty_template_type_id}
                                onChange={(_, __, opt) => handleFormChange('warranty_template_type_id', opt)}
                                styles={formErrors.warranty_template_type_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                required
                            />
                            {formErrors.warranty_template_type_id && <FormFeedback style={{ display: 'block' }}>{formErrors.warranty_template_type_id}</FormFeedback>}
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.category')}</StyledLabel>
                            <AsyncSearchableSelect
                                cacheOptions
                                defaultOptions={categories}
                                loadOptions={loadCategoryOptions}
                                value={formData.category_id}
                                onChange={(_, __, opt) => handleFormChange('category_id', opt)}
                                styles={formErrors.category_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                isClearable
                                required
                            />
                            {formErrors.category_id && <FormFeedback style={{ display: 'block' }}>{formErrors.category_id}</FormFeedback>}
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.applicabilityType')}</StyledLabel>
                            <SearchableSelect
                                options={applicabilityOptions}
                                value={formData.applicability_type}
                                onChange={(_, __, opt) => {
                                    handleFormChange('applicability_type', opt);
                                    handleFormChange('applicability_id', null);
                                }}
                                styles={formErrors.applicability_type ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                required
                            />
                            {formErrors.applicability_type && <FormFeedback style={{ display: 'block' }}>{formErrors.applicability_type}</FormFeedback>}
                        </StyledFormGroup>
                        {formData.applicability_type && formData.applicability_type.value !== 'all' && (
                            <StyledFormGroup>
                                <StyledLabel>{t('WarrantyTemplates.applicabilityId')}</StyledLabel>
                                <SearchableSelect
                                    options={getApplicabilityOptions()}
                                    value={formData.applicability_id}
                                    onChange={(_, __, opt) => handleFormChange('applicability_id', opt)}
                                    styles={formErrors.applicability_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                    required
                                />
                                {formErrors.applicability_id && <FormFeedback style={{ display: 'block' }}>{formErrors.applicability_id}</FormFeedback>}
                            </StyledFormGroup>
                        )}
                        <StyledFormGroup check>
                            <StyledLabel check>
                                <Input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={e => handleFormChange('is_active', e.target.checked)}
                                />
                                {t('WarrantyTemplates.isActive')}
                            </StyledLabel>
                        </StyledFormGroup>
                        <SaveButton type="submit" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : t('WarrantyTemplates.submit')}
                        </SaveButton>
                    </StyledForm>
                </Modal.Body>
            </Modal>

            {/* Edit Modal */}
            <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t('WarrantyTemplates.editTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {formErrors.general && <FormFeedback style={{ display: 'block', marginBottom: '10px' }} invalid>{formErrors.general}</FormFeedback>}
                    <StyledForm onSubmit={handleEditSubmit}>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.name')}</StyledLabel>
                            <Input
                                type="text"
                                value={formData.name}
                                onChange={e => handleFormChange('name', e.target.value)}
                                invalid={!!formErrors.name}
                                required
                            />
                            <FormFeedback>{formErrors.name}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.description')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.description}
                                onChange={e => handleFormChange('description', e.target.value)}
                            />
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.durationMonths')}</StyledLabel>
                            <Input
                                type="number"
                                value={formData.duration_months}
                                onChange={e => handleFormChange('duration_months', e.target.value)}
                                invalid={!!formErrors.duration_months}
                                required
                            />
                            <FormFeedback>{formErrors.duration_months}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.terms')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.terms}
                                onChange={e => handleFormChange('terms', e.target.value)}
                                invalid={!!formErrors.terms}
                                placeholder='{"key": "value"}'
                            />
                            <FormFeedback>{formErrors.terms}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.claimProcess')}</StyledLabel>
                            <Input
                                type="textarea"
                                value={formData.claim_process}
                                onChange={e => handleFormChange('claim_process', e.target.value)}
                                invalid={!!formErrors.claim_process}
                                placeholder='{"step": "description"}'
                            />
                            <FormFeedback>{formErrors.claim_process}</FormFeedback>
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.type')}</StyledLabel>
                            <SearchableSelect
                                options={templateTypes}
                                value={formData.warranty_template_type_id}
                                onChange={(_, __, opt) => handleFormChange('warranty_template_type_id', opt)}
                                styles={formErrors.warranty_template_type_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                required
                            />
                            {formErrors.warranty_template_type_id && <FormFeedback style={{ display: 'block' }}>{formErrors.warranty_template_type_id}</FormFeedback>}
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.category')}</StyledLabel>
                            <AsyncSearchableSelect
                                cacheOptions
                                defaultOptions={categories}
                                loadOptions={loadCategoryOptions}
                                value={formData.category_id}
                                onChange={(_, __, opt) => handleFormChange('category_id', opt)}
                                styles={formErrors.category_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                isClearable
                                required
                            />
                            {formErrors.category_id && <FormFeedback style={{ display: 'block' }}>{formErrors.category_id}</FormFeedback>}
                        </StyledFormGroup>
                        <StyledFormGroup>
                            <StyledLabel>{t('WarrantyTemplates.applicabilityType')}</StyledLabel>
                            <SearchableSelect
                                options={applicabilityOptions}
                                value={formData.applicability_type}
                                onChange={(_, __, opt) => {
                                    handleFormChange('applicability_type', opt);
                                    handleFormChange('applicability_id', null);
                                }}
                                styles={formErrors.applicability_type ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                required
                            />
                            {formErrors.applicability_type && <FormFeedback style={{ display: 'block' }}>{formErrors.applicability_type}</FormFeedback>}
                        </StyledFormGroup>
                        {formData.applicability_type && formData.applicability_type.value !== 'all' && (
                            <StyledFormGroup>
                                <StyledLabel>{t('WarrantyTemplates.applicabilityId')}</StyledLabel>
                                <SearchableSelect
                                    options={getApplicabilityOptions()}
                                    value={formData.applicability_id}
                                    onChange={(_, __, opt) => handleFormChange('applicability_id', opt)}
                                    styles={formErrors.applicability_id ? { control: base => ({ ...base, borderColor: '#dc3545' }) } : {}}
                                    required
                                />
                                {formErrors.applicability_id && <FormFeedback style={{ display: 'block' }}>{formErrors.applicability_id}</FormFeedback>}
                            </StyledFormGroup>
                        )}
                        <StyledFormGroup check>
                            <StyledLabel check>
                                <Input
                                    type="checkbox"
                                    checked={formData.is_active}
                                    onChange={e => handleFormChange('is_active', e.target.checked)}
                                />
                                {t('WarrantyTemplates.isActive')}
                            </StyledLabel>
                        </StyledFormGroup>
                        <SaveButton type="submit" disabled={loading}>
                            {loading ? <Spinner size="sm" /> : t('WarrantyTemplates.submit')}
                        </SaveButton>
                    </StyledForm>
                </Modal.Body>
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('WarrantyTemplates.deleteTitle')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{t('WarrantyTemplates.deleteConfirm', { name: selectedTemplate?.name })}</p>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
                        <MainButton variant="secondary" onClick={() => setShowDeleteModal(false)}>
                            {t('WarrantyTemplates.cancel')}
                        </MainButton>
                        <MainButton variant="destructive" onClick={handleDeleteConfirm} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : t('WarrantyTemplates.delete')}
                        </MainButton>
                    </div>
                </Modal.Body>
            </Modal>
        </>
    );
};

export default WarrantyTemplatesList;

// Styled Components
const ControlsContainer = styled.div`
    padding: 1rem;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid #e5e7eb;
    gap: 1rem;

    @media (max-width: 768px) {
        flex-direction: column;
        align-items: stretch;
    }
`;

const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    padding: 0.5rem 0.75rem;
    width: 300px;
    max-width: 100%;

    svg {
        color: #9ca3af;
        margin-right: 0.5rem;
    }

    input {
        border: none;
        background: transparent;
        outline: none;
        font-size: 0.875rem;
        color: #111827;
        width: 100%;

        &::placeholder {
            color: #9ca3af;
        }
    }
    
    &:focus-within {
        border-color: #701a82;
        box-shadow: 0 0 0 1px #701a82;
    }
`;

const EmptyState = styled.div`
    padding: 3rem;
    text-align: center;
    color: #6b7280;
    font-size: 0.875rem;
`;

const ActionContainer = styled.div`
    display: flex;
    gap: 0.5rem;
`;

const ActionButton = styled.button`
    border: none;
    background: transparent;
    cursor: pointer;
    color: #6b7280;
    padding: 0.25rem;
    border-radius: 4px;
    transition: color 0.2s, background 0.2s;

    &:hover {
        color: #701a82;
        background: #f3e8f4;
    }

    &.delete:hover {
        color: #ef4444;
        background: #fee2e2;
    }
`;

const StyledLabel = styled.label`
    display: block;
    margin-bottom: 0.375rem;
    font-size: 0.875rem;
    font-weight: 500;
    color: #374151;
`;

const StyledInput = styled(Input)`
    display: block;
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    line-height: 1.5;
    color: #111827;
    background-color: #fff;
    background-clip: padding-box;
    border: 1px solid #d1d5db;
    border-radius: 6px;
    transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;

    &:focus {
        border-color: #701a82;
        outline: 0;
        box-shadow: 0 0 0 3px rgba(112, 26, 130, 0.1);
    }
`;

const StyledForm = styled.form`
    display: flex;
    flex-direction: column;
    gap: 0.9375rem;
`;

const StyledFormGroup = styled.div`
    margin-bottom: 0.9375rem;
`;

const SaveButton = styled.button`
    background-color: rgba(130, 22, 146, 1);
    color: white;
    padding: 0.625rem 1.25rem;
    border: none;
    border-radius: 5px;
    font-weight: bold;
    cursor: pointer;
    transition: background-color 0.3s ease;
    &:hover {
        background-color: rgba(110, 12, 136, 1);
    }
`;

const LoadingSpinner = styled.div`
    text-align: center;
    font-size: 1.2rem;
    color: rgba(130, 22, 146, 1);
`;
